import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiBook,
    FiCheckSquare,
    FiClock,
    FiDollarSign,
    FiPlus,
    FiArrowRight,
    FiAlertCircle,
    FiRepeat,
    FiBookOpen,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import api from "../../../services/api";
import CollegeLogoBadge from "../../../components/common/CollegeLogoBadge";

export default function LibrarianDashboard() {
    const { user, collegeName } = useApp();
    const navigate = useNavigate();

    const [stats, setStats] = useState({
        totalBooks: 0,
        totalIssued: 0,
        overdueCount: 0,
        totalFinesPending: 0,
    });
    const [recentIssues, setRecentIssues] = useState([]);
    const [overdueList, setOverdueList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const [booksRes, issuesRes, finesRes] = await Promise.allSettled([
                    api.get("/library/books", { params: { limit: 1 } }),
                    api.get("/library/issues", { params: { limit: 10, status: "Issued" } }),
                    api.get("/library/fines", { params: { status: "Pending" } }),
                ]);

                const totalBooks =
                    booksRes.status === "fulfilled"
                        ? booksRes.value.pagination?.total || booksRes.value.data?.total || booksRes.value.data?.length || 0
                        : 0;

                const issueDocs =
                    issuesRes.status === "fulfilled"
                        ? Array.isArray(issuesRes.value.data)
                            ? issuesRes.value.data
                            : issuesRes.value.data?.data || []
                        : [];

                const finesDocs =
                    finesRes.status === "fulfilled"
                        ? Array.isArray(finesRes.value.data)
                            ? finesRes.value.data
                            : finesRes.value.data?.data || []
                        : [];

                const todayStr = new Date().toISOString().slice(0, 10);
                const overdue = issueDocs.filter((item) => item.dueDate && item.dueDate < todayStr && item.status !== "Returned");
                const totalFineAmt = finesDocs.reduce((sum, item) => sum + (Number(item.amount || item.fineAmount) || 0), 0);

                setStats({
                    totalBooks,
                    totalIssued: issueDocs.length,
                    overdueCount: overdue.length,
                    totalFinesPending: totalFineAmt,
                });
                setRecentIssues(issueDocs.slice(0, 5));
                setOverdueList(overdue.slice(0, 5));
            } catch (err) {
                toast.error(err.message || "Failed to load library dashboard data");
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading library dashboard…
            </div>
        );
    }

    return (
        <div>
            {/* ── Page Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {collegeName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>Welcome back, {user?.displayName || user?.username} 📚</h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>Library Operations &amp; Circulation Management</p>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/librarian/issues")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiRepeat size={14} /> Issue / Return
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/librarian/books/add")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiPlus size={14} /> Add New Book
                    </button>
                </div>
            </div>

            {/* ── Overdue Notice Banner ── */}
            {stats.overdueCount > 0 && (
                <div
                    style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        padding: "0.85rem 1.1rem",
                        marginBottom: "1.25rem",
                        fontSize: 13,
                        color: "#991b1b",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FiAlertCircle size={16} />
                        <span>
                            There are <strong>{stats.overdueCount} book(s)</strong> past their scheduled due date.
                        </span>
                    </div>
                    <button
                        className="btn btn-sm"
                        onClick={() => navigate("/librarian/issues?filter=overdue")}
                        style={{ background: "#dc2626", color: "#fff", border: "none", padding: "4px 10px", fontSize: 12 }}
                    >
                        Review Overdue
                    </button>
                </div>
            )}

            {/* ── KPI Metric Cards ── */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                    gap: "1rem",
                    marginBottom: "1.5rem",
                }}
            >
                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/librarian/books")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Total Books
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiBook size={15} color="#0284c7" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.totalBooks.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#0284c7", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        View Catalog <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/librarian/issues")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Active Borrowed
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiCheckSquare size={15} color="#7c3aed" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.totalIssued.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#7c3aed", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Active Circulation <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/librarian/issues")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Overdue Returns
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiClock size={15} color="#dc2626" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: stats.overdueCount > 0 ? "#dc2626" : "#16a34a" }}>
                        {stats.overdueCount}
                    </div>
                    <div style={{ fontSize: 11.5, color: stats.overdueCount > 0 ? "#dc2626" : "var(--text-muted)", marginTop: 4 }}>
                        {stats.overdueCount > 0 ? "Requires action" : "All books on time ✓"}
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/librarian/fines")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Pending Fines
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiDollarSign size={15} color="#059669" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: stats.totalFinesPending > 0 ? "#d97706" : "#16a34a" }}>
                        ₹{stats.totalFinesPending.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#059669", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Fine Records <FiArrowRight size={10} />
                    </div>
                </div>
            </div>

            {/* ── Content Grid: Recent Issues & Overdue Items ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.25rem" }}>
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiBookOpen size={14} color="#0284c7" /> Recent Book Issues
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/librarian/issues")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                        >
                            View All <FiArrowRight size={11} />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {recentIssues.length === 0 ? (
                            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                No recent issue records found.
                            </div>
                        ) : (
                            recentIssues.map((item, idx) => (
                                <div
                                    key={item._id || idx}
                                    style={{
                                        padding: "0.75rem 1rem",
                                        borderBottom: "1px solid var(--border)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontSize: 13,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{item.book?.title || item.bookTitle || "Untitled Book"}</div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            {item.student?.fullName || item.borrowerName || "Student"} · Due: {item.dueDate || "N/A"}
                                        </div>
                                    </div>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            padding: "2px 8px",
                                            borderRadius: 99,
                                            background: item.status === "Returned" ? "#dcfce7" : "#e0f2fe",
                                            color: item.status === "Returned" ? "#15803d" : "#0369a1",
                                        }}
                                    >
                                        {item.status || "Issued"}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiClock size={14} color="#dc2626" /> Overdue Books
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/librarian/fines")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                        >
                            Collect Fines <FiArrowRight size={11} />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {overdueList.length === 0 ? (
                            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                No overdue books. All records up to date!
                            </div>
                        ) : (
                            overdueList.map((item, idx) => (
                                <div
                                    key={item._id || idx}
                                    style={{
                                        padding: "0.75rem 1rem",
                                        borderBottom: "1px solid var(--border)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontSize: 13,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600, color: "#991b1b" }}>{item.book?.title || item.bookTitle || "Untitled Book"}</div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            {item.student?.fullName || item.borrowerName} · Overdue since {item.dueDate}
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-outline btn-sm"
                                        onClick={() => navigate(`/librarian/issues`)}
                                        style={{ fontSize: 11, padding: "2px 8px" }}
                                    >
                                        Return
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}